import {
    ProductController, UserController, EmailController
} from 'controllers';

// functions
import { getBoxStatus } from 'shared/functions';
import { cancelLateBoxSubscription } from 'shared/functions';
import { handleCancelItemSubscription } from 'shared/functions';

export const getProduct = async (barcode) => {
    try {
        return await ProductController.getBarcodeOnly(barcode);
    } catch (error) {
        throw new Error(`Barcode ${barcode} not valid. Retry. If the problem persists, please give the garment to Incoming Goods.`)
    }
}

export const handleReturn = async (barcode) => {
    if (!barcode || barcode === '') return null;

    const validProduct = await validation(barcode);

    const customer = validProduct.currentUser ? await UserController.getUserById(validProduct.currentUser) : null;

    // STEP 1: handle product according to its status
    const product = await handleStatus(validProduct, customer);

    // STEP 2: Return the item
    await UserController.updateUserBoxItemByBarcode({ userId: product.currentUser, boxId: product.currentBox, barcode, payload: { status: 'returned' } });

    // STEP BOX: HANDLE THE BOX
    await handleReturnBox(product, customer);

    // STEP 3: Update the logs (wash + return)
    await handleLogs(product);

    // STEP 4: Return the product(cycle + 1, nullify currentUser and currentBox, status to “available”)
    await returnProduct(product);
}

const validation = async (barcode) => {
    const product = await ProductController.getBarcodeOnly(barcode);

    if (product.length === 0) throw new Error(`Barcode ${barcode} not valid. Retry. If the problem persists, please give the garment to Incoming Goods.`);

    return product;
}

const handleStatus = async (product, customer) => {

    const { status, barcode } = product;
    if (status === 'available') throw new Error(`The product ${barcode} is already available. You have nothing to do.`);


    // if status is "archived": send an email: should include barcode, reason and email of customer

    if (status === 'archived') {
        // get the last box and customer:
        const logs = await ProductController.getProductLogs(barcode);
        const lastLog = logs.find(x => x.toStorage === null);

        if (lastLog) {
            product.currentBox = lastLog.boxID;
            product.currentUser = lastLog.userID;
            const recipient = product.market === 'Sweden' ? 'diana@hackyourcloset.com' : 'claire@hackyourcloset.fr';
            await EmailController.sendEmail('Returns', recipient, `TEST Return archived item ${barcode}`, `Hello! <br/><br/>The product ${barcode} is now returned. So it wasn't lost afterall... <br/><br/>The customer was: ${lastLog.userEmail}.The reason for archiving was ${product.archived?.reason}<br/><br/> Have a good day!`);
        } else {
            await EmailController.sendEmail('Returns', recipient, `TEST Return archived item ${barcode}`, `Hello!<br/><br/>The product ${barcode} is now returned. The reason for archiving was ${product.archived?.reason}<br/><br/> Have a good day!`);

            // we stop here because the archived product normally don't have currentUser, so nothing to return or update else.
            return;
        }
    }

    // checking that we have the data
    if (!product.currentUser || !product.currentBox) throw new Error(`Error: take a screenshot of that message please and send it to Pamela. <br/><br/> barcode: ${barcode}, currentBox: ${product.currentBox}, currentUser: ${product.currentUser}`);

    // Check single_item_subscription in User doc and cancel it
    const subscriptionId = getUserItemSubscription(customer, product.barcode);

    if (subscriptionId) {
        await handleCancelItemSubscription(customer, subscriptionId);
    }

    return product;
}

const handleLogs = async (product) => {
    const { barcode, currentBox } = product;

    const todayTimestamp = +new Date();
    const logPayload = {
        processedReturn: todayTimestamp, toStorage: todayTimestamp
    };

    await ProductController.updateLogById({ barcode, currentBox, payload: logPayload });
}

const returnProduct = async (product) => {
    const { barcode } = product;

    const newCycles = product.cycles ? product.cycles + 1 : 1;

    const productPayload = { currentUser: null, currentBox: null, status: 'available', flagged: false };

    if (product.currentBox) productPayload.cycles = newCycles;
    await ProductController.updateProductFields({ barcode, payload: productPayload });

    const recipient = product.market === 'Sweden' ? 'diana@hackyourcloset.com' : 'claire@hackyourcloset.com';

    if (product.flagged) await EmailController.sendEmail('Returns', recipient, `Flagged item ${barcode}`, `Hello! <br/><br/>The product ${barcode} is now returned: Returns will give it to you very soon.<br/><br/> Have a good day!`);
}


const handleReturnBox = async (product, customer) => {
    const { currentBox, currentUser } = product;

    // STEP 1: Get and Update other items
    const items = await UserController.getUserBoxItems(currentUser, currentBox);
    await handleBoxItemsStatuses(customer, currentBox, items);


    // STEP 2: Update box status
    const box = await UserController.getUserBoxByBoxId(currentUser, currentBox);
    // STEP 2.A: is_charged ? cancel the subscription
    if (box.is_charged) {
        if (customer.stripe?.late_box_subscriptions) {
            await cancelLateBoxSubscription(customer, currentBox);
        }
    }


    // STEP 2.B: at least one ["at_customer", "kept", "missing", "pending"] ==> "partially_returned" : "returned"
    const boxStatus = await getBoxStatus(items);
    const boxPayload = { status: boxStatus };
    await ProductController.updateBoxInfo(currentUser, currentBox, boxPayload);
}

const getUserItemSubscription = (customer, barcode) => {
    const subscription = customer?.stripe?.single_item_subscriptions?.find(x => x.barcode === barcode);
    const subscriptionId = subscription?.subscription_id;
    return subscriptionId;
}

const handleBoxItemsStatuses = async (customer, currentBox, items) => {

    for (const item of items) {
        if (item.status === 'at_customer') {
            // if has a subscription ? "kept" : "missing"
            const keptItem = customer.stripe?.single_item_subscriptions?.find((sub) => sub.barcode === item.barcode);
            item.status = keptItem ? "kept" : "missing";
            // update item and product
            await ProductController.updateProductStatus(item.barcode, item.status);
            if (keptItem) {
                await ProductController.updateProductLog(item.barcode, item.boxId, "declared_kept");
            }
            await ProductController.updateProductLog(item.barcode, item.boxId, "declared_missing");
            await ProductController.updateItem(customer.id, currentBox, item.id, { status: item.status });
        }
        // other statuses: no changes
    };
}
